import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../../../../components/Modal';
import CloseButton from '../../../../../components/CloseButton/CloseButton';
import { useTranslation } from 'react-i18next';
import CompanyItem from '../CompanyItem/CompanyItem';
import Spinner from '../../../../../../common/components/Spinner';
import { CustomButton } from '../../../../../components/Button/Button';
import styles from './EditFontModal.module.scss';
import SearchComponent from '../../../../../components/SearchComponent/SearchComponent';
import Icon from '../../../../../components/Icon/Icon';
import useOutsideClick from '../../../../../hooks/useOutsideClick';
import cx from 'classnames';
import Toggle from '../../../../../components/Toggle/Toggle';
import { ENTER_KEY_CODE, ESCAPE_KEY_CODE } from '../../../../../constants/common';

const initFontName = { edit: false, newName: null, name: null };

const EditFontModal = ({ open, allCompanies, fontData, onClose, onSave }) => {
  const { t } = useTranslation();
  const [assignedCompanies, setAssignedCompanies] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [collapse, setCollapsed] = useState(false);
  const [isDefault, setIsDefault] = useState(fontData?.default);
  const [isChanged, setIsChanged] = useState(false);
  const [fontName, setFontName] = useState(initFontName);
  const inputRef = useRef(null);

  const searchWrapperRef = useRef();

  useOutsideClick(searchWrapperRef, () => {
    if (collapse) {
      setCollapsed(false);
    }
  });

  useOutsideClick(inputRef, () => {
    if (fontName.edit) {
      setFontName(prevState => ({ ...prevState, ...(prevState.newName.trim().length === 0 ? { newName: prevState.name } : {}), edit: false }));
    }
  });

  const loading = useMemo(() => fontData === null && assignedCompanies !== null, [
    fontData,
    assignedCompanies,
  ]);

  const companiesToShow = useMemo(() => {
    if (!collapse || !allCompanies?.length) {
      return null;
    }
    return allCompanies?.length
      ? allCompanies
        .filter(company => !assignedCompanies.find(item => item.id === company.id))
        .filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()))
      : [];
  }, [allCompanies, assignedCompanies, collapse, searchValue]);

  const handleSearching = value => {
    setSearchValue(value);
  };

  const handleSelectCompany = company => {
    setIsChanged(true);
    setCollapsed(false);
    setSearchValue('');
    setAssignedCompanies(prevState => [...prevState, company]);
  };

  const handleRemoveCompany = id => {
    setIsChanged(true);
    setAssignedCompanies(prevState => prevState.filter(item => item.id !== id));
  };

  const handleClose = () => {
    setFontName(initFontName);
    onClose();
  }

  const handleSave = useCallback(() => {
    let isCompaniesListEqual = assignedCompanies.length === fontData.companies.length;
    if (isCompaniesListEqual) {
      const sortedCompanies = fontData.companies.map(({ id }) => id).sort();
      isCompaniesListEqual = assignedCompanies.map(({ id }) => id).sort().every(function (value, index) {
        return value === sortedCompanies[index];
      });
    }
    onSave(
      fontData.id,
      isDefault === fontData.default ? null : isDefault,
      isCompaniesListEqual ? null : assignedCompanies.map(item => item.id),
      fontName.newName && fontName.newName !== fontData.name ? fontName.newName : null
    );
    handleClose();
  }, [onSave, fontData, isDefault, assignedCompanies, fontName, handleClose]);

  const handleSearchActive = () => {
    setCollapsed(true);
  };

  const toggleCollapse = () => {
    setCollapsed(prevState => !prevState);
  };

  const toggleDefault = () => {
    setIsDefault(prevState => !prevState);
    setIsChanged(true);
  };

  const onNameChange = (e) => {
    const newName = e.target.value;
    setFontName(prevState => ({ ...prevState, newName }));
  }

  const onRenameClick = useCallback(() => {
    setFontName(prevState => ({ edit: true, newName: prevState.newName ?? fontData.name, name: prevState.newName ?? fontData.name }));
  }, [fontData]);

  useEffect(() => {
    if (!open) {
      setIsChanged(false);
      setSearchValue('');
      setCollapsed(false);
    }
  }, [open]);

  useEffect(() => {
    if (fontData) {
      setAssignedCompanies(fontData.companies);
      setIsDefault(fontData.default);
    }
  }, [fontData]);

  useEffect(() => {

    const onKeyEventListener = (e) => {
      switch (e.keyCode) {
        case ENTER_KEY_CODE:
          setFontName(prevState => ({ ...prevState, ...(prevState.newName?.trim()?.length === 0 ? { newName: prevState.name } : {}), edit: false }));
          break;
        case ESCAPE_KEY_CODE:
          console.log('ESCAPE_KEY_CODE', inputRef.current);
          if (inputRef.current) {
            setFontName(prevState => ({ edit: false, newName: prevState.name }));
            inputRef.current = null;
          } else {
            // As close on Esc key is disabled for modal, we need to still 
            // support this behavior, when input filed is not focused 
            handleClose();
          }
          break;
        default: break;
      }
    }

    document.addEventListener('keydown', onKeyEventListener);

    return () => document.removeEventListener('keydown', onKeyEventListener);
  }, []);

  return (
    <Modal open={open} onClose={handleClose} closeOnEsc={false} className={styles.modalWrapper}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <h2>{t('company.settings.fonts.manage.title', { fontName: fontData?.name })}</h2>
          <CloseButton onClose={handleClose} />
        </div>
        {loading ? (
          <Spinner size={'3px'} />
        ) : (
          <div>
            <div className={styles.nameWrapper}>
              {fontName.edit ? <input
                style={{ fontFamily: `'${fontData?.name}'` }}
                autoFocus={true}
                className={styles.renameInput}
                value={fontName.newName}
                onChange={onNameChange}
                maxLength={50}
                ref={inputRef}
              /> : <span className={styles.fontName} style={{ fontFamily: `'${fontData?.name}'` }}>
                {fontName?.newName ?? fontData?.name}
              </span>}
              {!fontName.edit && <CustomButton variant="link" className={styles.editBtn} onClick={onRenameClick}>
                <Icon src="pencil" className={styles.pencilIcon} />
              </CustomButton>}
            </div>
            <div className={styles.isDefaultContainer}>
              <Toggle checked={isDefault} onChange={toggleDefault} className={styles.toggle} />
              <span>{t('company.settings.fonts.manage.set_default')}</span>
            </div>
            <div className={cx(styles.companiesWrapper, { [styles.disabled]: isDefault })}>
              <div className={styles.addCompanyContainer}>
                <h3>{t('company.settings.fonts.manage.add_company')}</h3>
                <div className={styles.searchComponentWrapper} ref={searchWrapperRef}>
                  <SearchComponent inputClassName={styles.inputStyles} searchValue={searchValue} onChange={handleSearching} alwaysShow onFocus={handleSearchActive} />
                  <CustomButton className={cx(styles.arrowButton)} variant="link" onClick={toggleCollapse} ariaLabel="collapse">
                    <Icon src="arrowDown" className={cx(styles.arrowIcon, { [styles.collapsed]: collapse })} />
                  </CustomButton>
                  {companiesToShow ? (
                    <ul className={cx(styles.companiesList, { [styles.active]: collapse || searchValue?.length })}>
                      {companiesToShow?.length ? (
                        companiesToShow.map(item => (
                          <li key={item.id} className={cx(styles.companyItem)} onClick={() => handleSelectCompany(item)}>
                            {item.name}
                          </li>
                        ))
                      ) : (
                        <span>{`${t('dashboard.searching.no_matches')} '${searchValue}'`}</span>
                      )}
                    </ul>
                  ) : null}
                </div>
              </div>
              <div className={cx(styles.companiesListContainer, { [styles.empty]: !loading && !assignedCompanies?.length })}>
                {assignedCompanies?.length ? (
                  assignedCompanies?.map(({ id, name }) => (
                    <CompanyItem key={id} id={id} name={name} handleDelete={handleRemoveCompany} />
                  ))
                ) : loading ? null : (
                  <span className={styles.noCompaniesText}>{t('company.settings.fonts.manage.no_companies_assigned')}</span>
                )}
              </div>
            </div>
          </div>
        )}
        <div className={styles.buttonContainer}>
          <CustomButton variant="secondary" label={t('general.cancel')} className={styles.controlBtn} onClick={handleClose} />
          <CustomButton
            variant="primary"
            label={t('general.save')}
            className={styles.controlBtn}
            onClick={handleSave}
            disabled={!isChanged && (fontName?.newName?.trim().length === 0 || fontName?.newName === null || fontName?.newName === fontData?.name)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditFontModal;
